import React, { useRef, useEffect, useState } from "react"
import Helmet from "react-helmet"
import qs from "query-string"
import * as yup from "yup"

export function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef()

  // Store current value in ref
  useEffect(() => {
    ref.current = value
  }, [value]) // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current
}

export function PrintStyleSheet() {
  return (
    <Helmet
      style={[
        {
          cssText: `      
            @page {
              margin-top: 75px;
              margin-bottom: 50px;
            }

            @page:first {
              margin-top: 50px;
              margin-bottom: 50px;
            }

            @media print {
              #contentColumns div:nth-child(2) {
                margin: 0 auto;
              }

              #social-sharer, #article-language-selector, #site-header-items, #footer-margin, #popup-trigger, #recommended-footer, a[href="#script-holder"], a[href="/fundraiser?"], .page-footer-links, #briefing-footer, #pi-page-content > div:last-child, #pi-page-content > div:nth-last-child(2) {
                display: none;
              }
            }
          `,
        },
      ]}
    />
  )
}

export function ArticleStyleSheet() {
  return (
    <Helmet
      style={[
        {
          cssText: `      
            .gatsby-resp-image-figure {
              margin-left: 0;
              margin-right: 0;
              color: #280000;
              font-family: "IBM Plex Mono", Menlo, monospace;
              font-size: 0.7em;
              line-height: 130%;
            }

            .gatsby-resp-image-figure .gatsby-resp-image-figcaption {
              margin-top: 0.5em;
            }
          `,
        },
      ]}
    />
  )
}

export const isPosStrInt = (value: string) => {
  var n = Math.floor(Number(value))
  return n !== Infinity && String(n) === value && n > 0
}

export const updateQueryString = (key: string, value: string) => {
  if (window) {
    const url = new URL(window.location.href)
    url.searchParams.set(key, value)
    window.history.pushState(null, "", url.toString())
  }
}

export const SearchStringOrUndefined = yup.lazy((value) => {
  switch (typeof value) {
    case "undefined":
      return yup.string().default("")
    case "string":
      return yup
        .string()
        .label("Search term")
        .required("Please enter a keyword to search")
        .min(2, "Keyword must be at least two characters long")
        .max(50, "Keywords must be less than 50 characters long")
    default:
      throw new yup.ValidationError(
        "Must be a valid search string or undefined."
      )
  }
})

export const replacePullquotes = (value: string) => {
  try {
    const inlines = value.match(/\[\[pullquote\s\|\s(.*?)\]\]/g)

    if (!inlines) return value

    let tempValue = (" " + value).slice(1)

    const updatedInlines = inlines?.map((inline) => {
      const matchGroups = inline.match(/\[\[pullquote\s\|\s(.*?)\]\]/)

      if (!matchGroups || matchGroups.length < 2)
        throw new Error("Text content of pullquote not identified.")

      const textContent = matchGroups[1]

      return `<p class="pullquote">${textContent}</p>`
    })

    inlines.forEach((inline, i) => {
      tempValue = tempValue.replace(inline, updatedInlines[i])
    })

    return tempValue
  } catch (e) {
    return value
  }
}
